import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getMuiTheme } from '../styles/muiTheme';
import { syncStatusBar } from '../platform';

export const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} });

/**
 * Unified ThemeProvider that syncs:
 * 1. CSS variables via data-theme attribute
 * 2. MUI components via MuiThemeProvider
 *
 * This ensures both CSS-based components and MUI components
 * respect the same dark/light theme setting.
 */
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme');
      if (stored) return stored;
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  });

  // Update CSS variable theme
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('theme', theme);
    // iOS shell: keep status-bar text legible against the app theme (no-op on web)
    syncStatusBar(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  // Memoize MUI theme to prevent unnecessary re-renders
  const muiTheme = useMemo(() => getMuiTheme(theme), [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
